import styled, { css } from 'styled-components'
import { ThemeType } from 'styles/theme'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'full' | 'auto'
export type ButtonRadius = number | 'pill' | 'full'

interface StyledButtonProps {
  theme: ThemeType
}

interface ButtonProps {
  variant?: ButtonVariant
  size?: ButtonSize
  radius?: ButtonRadius
}

const getButtonStyles = (variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      // 既存のオレンジ背景ボタン
      return css`
        background-color: ${(p: StyledButtonProps) =>
          p.theme.MAIN_BACKGROUND_V2};
        color: ${(p: StyledButtonProps) => p.theme.FONT_COLOR};
        border-radius: 3px;
        font-weight: 700;
      `
    case 'secondary':
      // 新しいグレー背景ボタン（pill shape）
      return css`
        background-color: ${(p: StyledButtonProps) =>
          p.theme.BACKGROUND_SURFACE};
        color: ${(p: StyledButtonProps) => p.theme.FONT_COLOR};
        border-radius: 900px;
        box-shadow: none;
        border: 1px solid ${(p: StyledButtonProps) => p.theme.BORDER};
        font-weight: 700;
      `
    case 'ghost':
      // ボーダーなしのテキストリンク風ボタン
      return css`
        background-color: transparent;
        color: ${(p: StyledButtonProps) => p.theme.FONT_COLOR};
        border: none;
        box-shadow: none;
        font-weight: 400;

        &:hover {
          text-decoration: underline;
        }
      `
    default:
      return css``
  }
}

const getSizeStyles = (size: ButtonSize) => {
  switch (size) {
    case 'sm':
      return css`
        padding: 11px 12px;
        font-size: 12px;
        line-height: 16px;
        width: auto;
      `
    case 'md':
      return css`
        padding: 8px 16px;
        font-size: 14px;
        line-height: 18px;
        width: auto;
      `
    case 'lg':
      return css`
        padding: 12px 24px;
        font-size: ${(p) => p.theme.font.size.m};
        line-height: 16px;
        width: auto;
      `
    case 'full':
      return css`
        padding: 12px 24px;
        font-size: ${(p) => p.theme.font.size.m};
        line-height: 20px;
        width: 100%;
      `
    case 'auto':
      return css`
        padding: 8px 16px;
        font-size: 14px;
        line-height: 18px;
        width: auto;
      `
    default:
      return css`
        padding: 8px 16px;
        font-size: 14px;
        line-height: 18px;
        width: auto;
      `
  }
}

const getRadiusStyles = (radius?: ButtonRadius) => {
  if (radius === undefined) return css``

  if (typeof radius === 'number') {
    return css`
      border-radius: ${radius}px;
    `
  }

  switch (radius) {
    case 'pill':
      return css`
        border-radius: 900px;
      `
    case 'full':
      return css`
        border-radius: 9999px;
      `
    default:
      return css``
  }
}

const ButtonV2 = styled.button<ButtonProps & StyledButtonProps>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${(props) => getButtonStyles(props.variant || 'primary')}
  ${(props) => getSizeStyles(props.size || 'md')}
  ${(props) => getRadiusStyles(props.radius)}
`

export default ButtonV2
